/**
 * CommissioningHistoryPage — paginated, filterable, sortable history of
 * every commissioning record visible to the caller. Staff see everything;
 * customer-side users see records on compressors their company owns + any
 * record they personally signed.
 *
 * Route: /commissioning/history
 *
 * Drives: GET /api/commissioning/list (server-side filters + rollups).
 * Click any row → navigates to /commissioning/{id} for the full record.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700",
  submitted: "bg-emerald-100 text-emerald-800",
};
const ENG_TONE = {
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  needs_changes: "bg-orange-100 text-orange-800",
};

export default function CommissioningHistoryPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [data, setData] = useState({ items: [], total: 0, rollups: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    brand: "",
    has_imbalance: "",
    is_vsd: "",
    eng_review_status: "",
    sort: "-started_at",
  });
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: PER_PAGE, offset: page * PER_PAGE };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/commissioning/list", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters, page]);

  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(0); };

  const exportXlsx = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await api.get(`/commissioning/export.xlsx?${params.toString()}`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `commissionings_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / PER_PAGE)),
    [data.total],
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="history-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title="Home">
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Wrench size={20} weight="fill" className="text-emerald-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="history-title">
          {t("commissioning_history.title", "Commissioning History")}
        </h1>
        <button data-testid="history-new" onClick={() => navigate("/commissioning")}
          className="ml-auto px-3 py-1.5 bg-emerald-600 text-white rounded text-sm font-bold hover:bg-emerald-700">
          + New
        </button>
        <button data-testid="history-export-xlsx" onClick={exportXlsx}
          title={t("commissioning_history.export_tip", "Export current filter to Excel")}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">
          ↓ Export
        </button>
        <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* ─── Rollups bar ─────────────────────────────────────── */}
        {data.rollups && (
          <div data-testid="history-rollups" className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
            <Roll label="Total" value={data.rollups.total} testid="roll-total"
              tip={t("commissioning_history.tip_total", "All commissioning records you can see (drafts + submitted, every brand, every status).")} />
            <Roll label="Submitted" value={data.rollups.by_status?.submitted} tone="emerald" testid="roll-submitted"
              tip={t("commissioning_history.tip_submitted", "Records the technician has finished and submitted — locked from edits and visible to engineering for review.")} />
            <Roll label="Draft" value={data.rollups.by_status?.draft} tone="slate" testid="roll-draft"
              tip={t("commissioning_history.tip_draft", "Records still in progress — the technician hasn't submitted them yet. Click a row to keep editing.")} />
            <Roll label="VSD" value={data.rollups.vsd_count} tone="violet" testid="roll-vsd"
              tip={t("commissioning_history.tip_vsd", "Records flagged as Variable Speed Drive commissionings — different electrical checklist applies.")} />
            <Roll label={t("commissioning_history.roll_imbalance", "Imbalance flagged")} value={data.rollups.imbalance_count} tone="red" testid="roll-imbalance"
              tip={t("commissioning_history.tip_imbalance", "Voltage imbalance > 2% between phases at start-up. These usually need an electrician to recheck the supply before warranty kicks in.")} />
            <Roll label={t("commissioning_history.roll_eng_pending", "Eng pending")} value={data.rollups.engineering?.pending_review} tone="amber" testid="roll-pending-eng"
              tip={t("commissioning_history.tip_eng_pending", "Records waiting for an engineering reviewer to approve, request changes, or reject. Affects the dealer's quality scorecard.")} />
          </div>
        )}

        {/* ─── Filters ─────────────────────────────────────────── */}
        <div className="bg-white border rounded-md p-3 grid grid-cols-1 md:grid-cols-6 gap-2 text-sm">
          <input data-testid="history-q"
            value={filters.q} onChange={e => setFilter("q", e.target.value)}
            placeholder={t("commissioning_history.search_placeholder", "Search SN / company / signer")}
            title={t("commissioning_history.tip_search", "Search by serial number, customer / company name, or the signing technician's name.")}
            className="md:col-span-2 border rounded px-2 py-1" />
          <Select data-testid="history-status" value={filters.status}
            onChange={v => setFilter("status", v)}
            title={t("commissioning_history.tip_status", "Filter by record state — Draft (still editable) or Submitted (locked, sent to engineering).")}
            options={[["", "All statuses"], ["draft", "Draft only"], ["submitted", "Submitted only"]]} />
          <Select data-testid="history-brand" value={filters.brand}
            onChange={v => setFilter("brand", v)}
            title={t("commissioning_history.tip_brand", "Filter by compressor brand — US Air Compressor or Air Compressor Canada.")}
            options={[["", "All brands"], ["US", "US"], ["ACC", "ACC"]]} />
          <Select data-testid="history-vsd" value={filters.is_vsd}
            onChange={v => setFilter("is_vsd", v)}
            title={t("commissioning_history.tip_vsd_filter", "Filter by Variable Speed Drive flag — VSD units follow a different commissioning checklist.")}
            options={[["", "VSD: any"], ["1", "VSD only"], ["0", "Non-VSD"]]} />
          <Select data-testid="history-eng" value={filters.eng_review_status}
            onChange={v => setFilter("eng_review_status", v)}
            title={t("commissioning_history.tip_eng_filter", "Filter by engineering-review verdict — Pending, Approved, Rejected, or Needs changes.")}
            options={[["", "Eng review: any"], ["pending_review", "Pending"], ["approved", "Approved"],
                     ["rejected", "Rejected"], ["needs_changes", "Needs changes"]]} />
        </div>

        {/* ─── Table ───────────────────────────────────────────── */}
        <div className="bg-white border rounded-md overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="history-table">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <Th label="Serial" sortKey="serial_number" filters={filters} setFilter={setFilter} />
                <Th label="Product" />
                <Th label="Owner" sortKey="company_name" filters={filters} setFilter={setFilter} />
                <Th label="Brand" />
                <Th label="Seq #" sortKey="sequence_number" filters={filters} setFilter={setFilter} />
                <Th label="Status" />
                <Th label="Started" sortKey="started_at" filters={filters} setFilter={setFilter} />
                <Th label="Voltage" />
                <Th label={t("commissioning_history.col_eng_review", "Eng review")} />
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">Loading…</td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-slate-500">{t("commissioning_history.empty", "No commissionings match the current filters.")}</td></tr>
              )}
              {!loading && data.items.map(it => {
                const v = it.measurements?.voltage || {};
                const v_summary = [
                  v.l1_l2 != null ? `L1↔L2 ${v.l1_l2}V` : "",
                  v.l2_l3 != null ? `L2↔L3 ${v.l2_l3}V` : "",
                  v.l1_l3 != null ? `L1↔L3 ${v.l1_l3}V` : "",
                ].filter(Boolean).join(" · ") || "—";
                const imbalance_pct = it.measurements?.voltage_imbalance_pct;
                const flag = it.measurements?.voltage_imbalance_flag;
                return (
                  <tr key={it.id} data-testid={`history-row-${it.id}`}
                    onClick={() => navigate(`/commissioning/${it.id}`, { state: { from: "/commissioning/history" } })}
                    className="hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-mono font-bold">{it.serial_number || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {it.product_name}
                      <div className="text-[10px] text-slate-500">
                        {it.phase_count}-phase
                        {it.is_vsd && " · VSD"}
                        {it.is_air_center && " · Air Center"}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs truncate max-w-[180px]">{it.company_name}</td>
                    <td className="px-3 py-2 text-xs">{it.brand}</td>
                    <td className="px-3 py-2 font-bold">#{it.sequence_number}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_TONE[it.status] || "bg-slate-100"}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {it.started_at ? new Date(it.started_at).toLocaleDateString() : "—"}
                      <div className="text-[10px] text-slate-500">{it.tech_email}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {v_summary}
                      {imbalance_pct != null && (
                        <div className={flag ? "text-red-700 font-bold" : "text-slate-500"}>
                          {imbalance_pct}% imbalance{flag && " ⚠"}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {it.engineering_review_status ? (
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${ENG_TONE[it.engineering_review_status] || "bg-slate-100"}`}>
                          {it.engineering_review_status.replace("_", " ")}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ─── Pagination ──────────────────────────────────────── */}
        {data.total > PER_PAGE && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">
              Showing {(page * PER_PAGE) + 1}–{Math.min((page + 1) * PER_PAGE, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <button data-testid="history-prev" onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 border rounded disabled:opacity-50">Prev</button>
              <span className="px-3 py-1.5">{page + 1} / {totalPages}</span>
              <button data-testid="history-next" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 border rounded disabled:opacity-50">{t("pagination.next", "Next")}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Roll({ label, value, tone = "blue", testid, tip }) {
  const tones = {
    blue: "border-blue-200 text-blue-800 bg-blue-50",
    emerald: "border-emerald-200 text-emerald-800 bg-emerald-50",
    slate: "border-slate-200 text-slate-700 bg-slate-50",
    violet: "border-violet-200 text-violet-800 bg-violet-50",
    red: "border-red-200 text-red-800 bg-red-50",
    amber: "border-amber-200 text-amber-800 bg-amber-50",
  };
  return (
    <div data-testid={testid} title={tip || undefined}
         className={`border rounded p-2 text-center ${tones[tone]} ${tip ? "cursor-help" : ""}`}>
      <div className="text-2xl font-bold font-['Chivo']">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
    </div>
  );
}

function Select({ value, onChange, options, title, "data-testid": testid }) {
  return (
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}
      title={title || undefined}
      className={`border rounded px-2 py-1 ${title ? "cursor-help" : ""}`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Th({ label, sortKey, filters, setFilter }) {
  if (!sortKey) return <th className="text-left px-3 py-2">{label}</th>;
  const cur = filters?.sort || "";
  const active = cur.replace(/^[+-]/, "") === sortKey;
  const dir = cur.startsWith("-") ? "desc" : "asc";
  const next = active ? (dir === "desc" ? sortKey : `-${sortKey}`) : `-${sortKey}`;
  return (
    <th className="text-left px-3 py-2">
      <button data-testid={`history-sort-${sortKey}`}
        onClick={() => setFilter("sort", next)}
        className={`hover:text-slate-900 ${active ? "text-slate-900" : ""}`}>
        {label} {active ? (dir === "desc" ? "↓" : "↑") : ""}
      </button>
    </th>
  );
}
